import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import * as PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import './Login.css';
import {
  Form, Icon, Input, Button, Row, Divider, Carousel,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import LocaleSelector from '../../components/translations/LocaleSelector';
import routeLocations from '../../utility/route-locations';
import AcceptTerms from './AcceptTerms';
import { selectAuthenticationRequest, selectCurrentLoginAddress } from '../../state/ui/login/loginUiReducer';
import { requestAuthenticateUser, setCurrentLoginAddress } from '../../state/ui/login/loginUiActions';
import { selectSessionUser } from '../../state/ui/application/sessionUiReducer';
import { userIsAdminConsole } from '../../utility/security/security-util';
import { ViaLogo } from '../../components/logo/ViaLogo';
import { asViaUrl } from '../../utility/via-fetch';


const createUsernameField = (form, intl, defaultValue) => form.getFieldDecorator('username', {
  initialValue: defaultValue,
  rules: [
    { required: true, message: intl.formatMessage({ id: 'validationEmail' }) },
    { type: 'email', message: intl.formatMessage({ id: 'invalidEmail' }) },
  ],
})(
  <Input
    prefix={<Icon type="user" style={{ color: 'rgba(0,0,0,0.25)' }} />}
    type="email"
    placeholder={intl.formatMessage({ id: 'email' })}
    maxLength={80}
  />,
);

// The root login component.
// Checks if the user is done with authentication and redirects them.
// Otherwise displays the login form.
function Login({ form }) {
  // Redux Actions
  const dispatch = useDispatch();
  const authenticateUser = useCallback((username, password) => dispatch(requestAuthenticateUser(username, password)), [dispatch]);
  const setLoginAddress = useCallback(email => dispatch(setCurrentLoginAddress(email)), [dispatch]);

  // Redux State
  const sessionUser = useSelector(selectSessionUser);
  const authenticationRequest = useSelector(selectAuthenticationRequest);
  const authenticating = authenticationRequest ? authenticationRequest.isLoading : false;
  const defaultLoginAddress = useSelector(selectCurrentLoginAddress);

  const location = useLocation();
  const intl = useIntl();
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (sessionUser) { return; }

    try {
      const values = await form.validateFields();
      authenticateUser(values.username, values.password);
    } catch (_e) {
      // Nothing to do, the form will show the errors visually.
    }
  }, [form, authenticateUser, sessionUser]);

  const [textVisible, setTextVisible] = useState(false);
  const suffixIcon = textVisible
    ? (<Icon type="eye-invisible" onClick={() => setTextVisible(false)} style={{ color: 'rgba(0,0,0,.5)' }} />)
    : (<Icon type="eye" onClick={() => setTextVisible(true)} style={{ color: 'rgba(0,0,0,.5)' }} />);
  const fieldType = textVisible ? 'text' : 'password';
  const carouselRef = useRef();

  const allTncsAcceptedServer = (sessionUser && !sessionUser.termsToAccept.termsOfUseToAccept
    && !sessionUser.termsToAccept.privacyPolicyToAccept && !sessionUser.termsToAccept.eulaToAccept);

  useEffect(() => {
    if (sessionUser && !allTncsAcceptedServer && carouselRef.current) {
      carouselRef.current.goTo(1);
    }
  }, [carouselRef, sessionUser, allTncsAcceptedServer]);

  useEffect(() => { setLoginAddress(form.getFieldValue('username')); }, [form, setLoginAddress]);

  if (allTncsAcceptedServer) {
    let sourceLocation = location.state ? location.state.sourceLocation : undefined;
    sourceLocation = userIsAdminConsole(sessionUser) ? routeLocations.adminConsole : sourceLocation;
    if (sourceLocation && !userIsAdminConsole(sessionUser) && sourceLocation.includes(routeLocations.adminConsole)) {
      sourceLocation = null;
    }
    // Do not use redirect, force a reload. This helps ensure the user gets any updates to the app.
    // It also helps make the translation loading more seamless if the user does not log in in their normal locale.
    window.location.assign(asViaUrl(sourceLocation || routeLocations.dashboard));
    return <div style={{ minHeight: '100vh' }} />;
  }

  return (
    <div className="app-login-layout">
      <Row type="flex" align="middle" justify="center">
        <div className="app-login-content">
          <Row type="flex" justify="center" className="app-login-logo">
            <ViaLogo href={routeLocations.landing} />
          </Row>
          <Carousel dots={false} ref={carouselRef} touchMove={false} swipe={false} infinite={false} draggable={false} accessibility={false}>
            <div>
              <Row>
                <Form onSubmit={e => handleSubmit(e)} className="app-login-form">
                  <Form.Item>{ createUsernameField(form, intl, defaultLoginAddress) }</Form.Item>
                  <Form.Item>
                    {
                      form.getFieldDecorator('password', {
                        rules: [
                          { required: true, message: intl.formatMessage({ id: 'validationPassword' }) },
                        ],
                      })(
                        <Input
                          prefix={<Icon type="lock" style={{ color: 'rgba(0,0,0,.25)' }} />}
                          type={fieldType}
                          placeholder={intl.formatMessage({ id: 'password' })}
                          maxLength={80}
                          suffix={suffixIcon}
                        />,
                      )
                    }
                  </Form.Item>
                  <Form.Item>
                    <Button
                      className="app-login-button"
                      type="primary"
                      block
                      htmlType="submit"
                      loading={authenticating}
                    >
                      <FormattedMessage id="login" />
                    </Button>
                    <Link className="login-form-forgot" to={routeLocations.forgotPassword}><FormattedMessage id="forgotPassword" /></Link>
                  </Form.Item>
                </Form>
              </Row>
              <Row style={{ marginBottom: '12px' }}>
                <FormattedMessage id="translatePage" />
              </Row>
              <Row type="flex" justify="center">
                <LocaleSelector />
              </Row>
              <Divider />
              <Row type="flex" justify="center">
                <FormattedMessage id="noAccount" />
                &nbsp;
                <Link className="login-form-sign-up" to={routeLocations.signUp}><FormattedMessage id="newAccountSignUp" /></Link>
              </Row>
            </div>
            <AcceptTerms carouselRef={carouselRef} />
          </Carousel>
        </div>
      </Row>
    </div>
  );
}


Login.propTypes = {
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
  }).isRequired,
};

export default Form.create({ name: 'main_login' })(Login);
